import React, { Component, Fragment, } from 'react';
import { useToggle, useAsync, useList, } from 'react-use';
import { sumBy, groupBy, keyBy, sortBy, omit, difference, } from 'lodash';
import { Button, Input, } from 'reactstrap';
import Select from 'react-select';
import qs from 'qs';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import numeral from 'numeral';

import firebase, { functions } from '../../firebase';
import { batch } from '../../shared/firebase';
import { accountItemCategoryNames, } from '../../shared/config';
import { numberFormat, floatFormat } from '../../util';
import { fields } from '../../shared/models/cash';
import { generateRows, computeAlerts, rowsForExport, } from '../../shared/lib/cashes';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCompanySelector from '../hooks/useCompanySelector';
import useQueryParams from '../hooks/useQueryParams';
import usePkgItems from '../hooks/usePkgItems';
import RelatedCompanyPage from '../hocs/RelatedCompanyPage';
import ModelFormModal from '../modals/ModelFormModal';
import CompanySyncButton from '../CompanySyncButton';
import TrialsSyncButton from '../TrialsSyncButton';
import AutoLinkText from '../AutoLinkText';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import ExportButton from '../ExportButton';
import ImportButton from '../ImportButton';
import ProgressButton from '../ProgressButton';
import CashesImportModalButton from '../CashesImportModalButton';
import DeleteButton from '../DeleteButton';
import QuerySelector from '../QuerySelector';
import AmountWithExchange from '../AmountWithExchange';
import Alerts from '../Alerts';

const db = firebase.firestore();

export default RelatedCompanyPage(function CompanyCashes (props) {
  const { relatedCompany, company, subsidiaryId, period, yearMonth, filteredYearMonths, cr, isLockedMonth, } = props;
  const cashesRef = company.ref.collection('cashes');
  const accountItems = useCollectionSubscription(company.ref.collection('accountItems').where('subsidiaryId', '==', subsidiaryId), [company, subsidiaryId]);
  const accountItemsByName = keyBy(accountItems, 'name');
  const { items: cashes } = usePkgItems(company, relatedCompany, subsidiaryId, accountItems, 'cashes', [], 'amount', [], filteredYearMonths);
  const trials = useCollectionSubscription(company.ref.collection('trials').where('subsidiaryId', '==', subsidiaryId).where('yearMonth', '==', yearMonth), [subsidiaryId, yearMonth]);
  const trialBalances = trials.flatMap(_ => _.balances);
  const importSetting = useDocumentSubscription(company.ref.collection('changesTableImportSettings').doc(`cashes_${subsidiaryId}`), [company, subsidiaryId], { onError: _ => console.log(_) });
  const rows = generateRows(cashes || [], accountItems, trials);
  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedIds, }] = useList([]);
  const unselectId = (_) => removeSelectedIdAt(selectedIds.indexOf(_));
  const isSelecting = selectedIds.length > 0;
  const isAllSelected = rows.length > 0 && difference(rows.map(_ => _.id), selectedIds).length === 0;
  const onClickBatchDelete = async () => {
    if(!window.confirm(`${selectedIds.length}件を削除します。よろしいですか？`)) return;

    await batch(db, selectedIds, (b, _) => b.delete(cashesRef.doc(_)));
    toast.success('削除しました');
    setSelectedIds([]);
  };
  const processImportRow = (batch, row) => {
    const { accountItemName, amount, note } = row;
    const accountItemId = accountItemsByName[accountItemName]?.id;
    if(!accountItemId) return;

    batch.set(cashesRef.doc(), {
      accountItemId,
      amount: numeral(amount).value() || 0,
      note: note || '',
      subsidiaryId,
      period,
      yearMonth,
      createdAt: new Date(),
    });
  };
  const alerts = computeAlerts(cashes || [], accountItems, trials);

  return props.translate(
    <div className="company-cashes">
      <Alerts alerts={alerts} />
      <div className="mb-3 d-flex justify-content-end gap-1">
        {
          isSelecting && (
            <Fragment>
              <span className="align-self-center">{selectedIds.length} 件を選択中</span>
              <ProgressButton color="danger" process={onClickBatchDelete} disabled={isLockedMonth}>
                削除
              </ProgressButton>
            </Fragment>
          )
        }
        <CashesImportModalButton
          company={company}
          itemsRef={cashesRef}
          trialBalances={trialBalances}
          accountItems={accountItems}
          accountItemsByName={accountItemsByName}
          subsidiaryId={subsidiaryId}
          period={period}
          yearMonth={yearMonth}
          existingItems={cashes || []}
          savedSetting={importSetting}
          disabled={isLockedMonth}
        />
        <ExportButton screenName="現金及び現金同等物" targetCompany={relatedCompany} rows={rowsForExport(cashes, accountItems, trials)} />
        <ImportButton processRow={processImportRow} importKey={`cashes-${subsidiaryId}-${yearMonth}`} disabled={isLockedMonth} />
        <AddButton itemRef={cashesRef.doc()} processValues={_ => ({ ..._, subsidiaryId, period, yearMonth, })} FormModal={ModelFormModal} formProps={{ title: '現金及び現金同等物 追加', documentName: 'generalPkgItem', fields: fields({ accountItems, otherItems: rows, }), }} disabled={isLockedMonth} />
      </div>
      <div>
        <table className="table sticky-table">
          <thead className="thead-light text-center">
            <tr>
              <th style={{ width: 40 }}>
                <Input
                  type="checkbox"
                  className="position-relative m-0"
                  checked={isAllSelected}
                  onChange={_ => isAllSelected ? setSelectedIds([]) : setSelectedIds(rows.map(_ => _.id))}
                />
              </th>
              <th style={{ minWidth: 200 }}>個別勘定科目</th>
              <th style={{ minWidth: 200 }}>金額</th>
              <th style={{ minWidth: 200 }}>試算表残高</th>
              <th style={{ minWidth: 250 }}>備考</th>
              <th style={{ minWidth: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {
              rows.map((cash) => {
                const { id, ref, accountItemId, accountItem, trialAmount, amount, exchangedAmounts, note, } = cash;

                return (
                  <tr key={id}>
                    <td>
                      <Input
                        type="checkbox"
                        className="position-relative m-0"
                        checked={selectedIds.includes(id)}
                        onChange={_ => selectedIds.includes(id) ? unselectId(id) : selectId(id)}
                      />
                    </td>
                    <td>
                      {accountItem && accountItem.name}
                    </td>
                    <td className={classnames('text-right', { 'table-danger': floatFormat(amount) !== floatFormat(trialAmount) })}>
                      <AmountWithExchange currency={relatedCompany.currency} amounts={[amount, exchangedAmounts.amount]} />
                    </td>
                    <td className="text-right">
                      <AmountWithExchange currency={relatedCompany.currency} amounts={[trialAmount, trialAmount * cr]} />
                    </td>
                    <td>
                      <AutoLinkText>
                        {note}
                      </AutoLinkText>
                    </td>
                    <td className="text-nowrap text-right">
                      <EditButton itemRef={ref} FormModal={ModelFormModal} formProps={{ title: '現金及び現金同等物 編集', documentName: 'generalPkgItem', fields: fields({ accountItems, otherItems: rows.filter(_ => _.id !== id), }), }} disabled={isLockedMonth} />
                      <DeleteButton item={cash} itemRef={ref} className="ml-2" disabled={isLockedMonth} />
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
      </div>
    </div>
  );
});
